import { EventEmitter } from '@spotify-internal/emitter';
import { Method } from '@spotify-internal/transport/lib/enums/method';
import { LOCAL_DEVICE_ID, GAIA_VOLUME, } from '../../typedefs';
import { DeviceType } from '../../enums/device_type';
import { HarmonyErrors } from '../../enums/errors';
import { PlayerAPIClientError } from '../../error/connect_service/client';
import { defer } from '../helpers/defer';
import { areDevicesEqual } from '../helpers/device';
/**
 * The default endpoint for the Connect State service.
 */
const ENDPOINT = '@webgate/connect-state';
/**
 * A regular expression filter used to match messages from dealer.
 */
const URI_FILTER = /connect-state\/v1\/cluster$/;
/**
 * Prefix for the observer device id.
 */
const OBSERVER_PREFIX = 'hobs_';
/**
 * A regular expression used to extract the first 40 characters of the device id.
 */
const DEVICE_ID_TRUNCATOR = /^([a-zA-Z0-9_%:-]{1,40}).*$/;
function isTransferrable(playerState) {
    var _a, _b, _c;
    if (!playerState.track) {
        return false;
    }
    return (((_c = (_b = (_a = playerState.restrictions) === null || _a === void 0 ? void 0 : _a.disallow_transferring_playback_reasons) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) === 0);
}
export class PlayerAPIClient extends EventEmitter {
    constructor(options) {
        super();
        /**
         * When set to true, the client will automatically register once it receives
         * a connection id.
         */
        this._autoregister = true;
        /**
         * The connection id
         *
         */
        this._connectionId = '';
        /**
         * The local device id.
         */
        this._localDeviceId = '';
        /**
         * The local device id for the observer (i.e., this instance).
         *
         * Since we cannot have the same device id for two different devices, and
         * since the observer is technically a different device, we need a to append
         * a short string to the end of the local device id to show that this is the
         * observer device.
         */
        this._localObserverDeviceId = null;
        /**
         * A reference to the last device that was part of a state-changed event.
         */
        this._lastActiveDevice = null;
        /**
         * A reference to the last player state received.
         */
        this._lastKnownPlayerState = null;
        /**
         * A cache of the last known list of devices.
         */
        this._lastKnownDevices = [];
        /**
         * A reference to the local device.
         */
        this._localDevice = null;
        /**
         * A flag that indicates whether the initial state has been fetched.
         */
        this._fetchedInitialState = false;
        /**
         * A map of requests that are awaiting acknowledgement from the service.
         */
        this._awaitingAcknowledgment = {};
        /**
         * An array of objects containing "acknowledgements" from the service that
         * are received while requests are in flight.
         *
         * Since the service can acknowledge a command using an `ack_id` even before
         * the request for a command has finished, we create request-specific maps for
         * all the commands acknowledged during the roundtrip of the request.
         *
         * Once the response of the command request is received, we can compare the
         * ack_id (if any) to any acknowledged commands while the request is in
         * flight.
         */
        this._acknowledgementMaps = [];
        /**
         * The timestamp of the last processed cluster data.
         */
        this._lastProcessedTimestamp = 0;
        this._descriptor = options.descriptor;
        this._transport = options.transport;
        this._endpoint = options.endpoint || ENDPOINT;
        this._autoregister =
            'autoregister' in options ? !!options.autoregister : true;
        this._onConnectionId = this._onConnectionId.bind(this);
        this._onClusterMessage = this._onClusterMessage.bind(this);
        this._awaitResponseWithAck = this._awaitResponseWithAck.bind(this);
        /**
         * Initializes the PlayerAPIClient instance.
         */
        this._transport.on(this._transport.EVENT_CONNECTION_ID, (e) => {
            this._onConnectionId(e.data.id);
        });
        this._transport.matchMessages(URI_FILTER, this._onClusterMessage);
        this._descriptorPromise = Promise.resolve(this._descriptor).then((descriptor) => {
            this._localDeviceId = descriptor.id || '';
            // Truncated to 40-chars
            this._localObserverDeviceId = (OBSERVER_PREFIX + this._localDeviceId).replace(DEVICE_ID_TRUNCATOR, '$1');
            return true;
        });
    }
    /**
     * Creates a new instance of the PlayerAPIClient.
     *
     * @param options - The options for the instance.
     * @return The PlayerAPI client.
     */
    static create(options) {
        return new PlayerAPIClient(options);
    }
    /**
     * Handles the 'connection_id' event from the transport.
     *
     * @param id - The connection id.
     */
    _onConnectionId(id) {
        this._connectionId = id;
        if (this._autoregister) {
            this.register();
        }
    }
    /**
     * Handles a cluster message from dealer.
     *
     * @param msg - The message from Dealer.
     */
    _onClusterMessage(msg) {
        const payload = msg.payloads && msg.payloads[0];
        if (!payload) {
            return;
        }
        if (payload.ack_id) {
            const id = payload.ack_id;
            const waiting = this._awaitingAcknowledgment[id];
            if (waiting) {
                // We know that there's a corresponding command awaiting an
                // acknowledgement, so we just resolve the promise.
                waiting.resolve(true);
            }
            else {
                // There's nothing waiting, but the requests in-flight might not know
                // their ack_ids yet, so we go through their maps and set the ack ids
                // as acknowledged. That way when they're done, they can resolve their
                // promises immediately.
                const acknowledgementMaps = this._acknowledgementMaps;
                for (let i = 0, l = acknowledgementMaps.length; i < l; i++) {
                    acknowledgementMaps[i][id] = defer();
                }
            }
        }
        this._parseCluster(payload.cluster);
    }
    /**
     * Builds an endpoint for a particular request.
     *
     * @param version - The version of the API to use.
     * @param command - The command for the request.
     * @param deviceId - The target device id of the request.
     * @return The generated endpoint.
     */
    _makeEndpoint(version, command, deviceId) {
        return this._descriptorPromise.then(() => {
            const localId = this._localDeviceId;
            let targetId = deviceId;
            if (!targetId) {
                // If no device id is provided, default to the last active one, if present
                // or to the local device.
                targetId = this._lastActiveDevice
                    ? this._lastActiveDevice.id
                    : this._localDeviceId;
            }
            // If endpoint Id is '@local', default to localDeviceId.
            if (targetId === LOCAL_DEVICE_ID) {
                targetId = localId;
            }
            return `${this._endpoint}/${version}/${command}/from/${localId}/to/${targetId}`;
        });
    }
    /**
     * Sends a player command to the service.
     *
     * @param endpoint - The command endpoint name.
     * @param deviceId - The target device id.
     * @param payload - payload The optional payload for the command.
     * @return A promise that will be resolved with a boolean
     * value indicating whether the command was successfully triggered.
     */
    _sendPlayerCommand(endpoint, deviceId = null, payload = {}) {
        payload.endpoint = endpoint;
        return this._makeEndpoint('v1', 'player/command', deviceId)
            .then((url) => this._sendRequest(Method.POST, url, { command: payload }))
            .then(this._awaitResponseWithAck);
    }
    /**
     * Sends a connect command to the service.
     *
     * @param endpoint - The command endpoint name.
     * @param deviceId - The target device id.
     * @param payload - The optional payload for the command.
     * @return - A promise that will be resolved with a boolean
     * value indicating whether the command was successfully triggered.
     */
    _sendConnectCommand(endpoint, deviceId = null, payload = {}) {
        const method = endpoint === 'volume' ? Method.PUT : Method.POST;
        return this._makeEndpoint('v1', `connect/${endpoint}`, deviceId)
            .then((url) => {
            return this._sendRequest(method, url, payload);
        })
            .then(this._awaitResponseWithAck);
    }
    /**
     * Sends a request to the Connect Controller service.
     *
     * @param method - The method of the request.
     * @param url - The URL path.
     * @param payload -The payload to send for the request.
     * @return - The response for the request.
     */
    _sendRequest(method, url, payload) {
        // When we start a request, we create an object that will serve as a map of
        // all of the commands acknowledged via state updates while the request is in
        // flight. This solves the racy behaviour where a command might be
        // acknowledged before we actually know the ID of the command (which we get
        // as a response to the command).
        const acknowledgementMaps = this._acknowledgementMaps;
        const map = {};
        acknowledgementMaps.push(map);
        const removeMap = () => {
            const index = acknowledgementMaps.indexOf(map);
            if (index !== -1) {
                acknowledgementMaps.splice(index, 1);
            }
        };
        return this._transport
            .request(url, {
            method: method,
            payload: payload ? JSON.stringify(payload) : undefined,
            responseType: 'json',
            retry: {
                condition: (response, StatusFamily) => {
                    const family = response.getStatusFamily();
                    return (family === StatusFamily.SERVER_ERROR ||
                        family === StatusFamily.CONNECTION_ERROR);
                },
            },
        })
            .then((response) => {
            // Append the acknowledged command as part of the response,
            // and then remove it from the array.
            removeMap();
            return { response, map };
        }, (e) => {
            removeMap();
            return Promise.reject(e);
        });
    }
    /**
     * Parses the response of the request and makes it wait if necessary.
     *
     * @param obj - The response to parse.
     * @return The promise.
     */
    _awaitResponseWithAck(obj) {
        // TODO: Move to request.metadata
        if (obj.response.status === 404) {
            return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_DEVICE, 'No such device.'));
        }
        else if (obj.response.status === 200 &&
            obj.response.body &&
            obj.response.body.ack_id) {
            const id = obj.response.body.ack_id;
            if (obj.map && obj.map[id]) {
                return Promise.resolve(true);
            }
            const deferred = defer();
            this._awaitingAcknowledgment[id] = deferred;
            return deferred.promise;
        }
        return Promise.resolve(obj.response.status === 200);
    }
    /**
     * Parses a Connect State Cluster object.
     *
     * @param cluster - The Cluster object from the Connect State service.
     */
    _parseCluster(cluster) {
        if (!cluster) {
            return;
        }
        const clusterTimestamp = parseInt(cluster.timestamp, 10);
        if (this._lastProcessedTimestamp > clusterTimestamp) {
            // Stale data, do not process.
            return;
        }
        this._lastProcessedTimestamp = clusterTimestamp;
        this._parseClusterDevices(cluster);
        this._parseClusterPlayerState(cluster);
    }
    /**
     * Parses the devices map from a Cluster object.
     *
     * @param cluster - The Cluster object from the Connect State service.
     */
    _parseClusterDevices(cluster) {
        const prevActiveDevice = this._lastActiveDevice;
        // Reset
        this._lastActiveDevice = null;
        this._lastKnownDevices = [];
        this._localDevice = null;
        const deviceIds = [];
        for (const deviceId in cluster.devices) {
            if (cluster.devices.hasOwnProperty(deviceId)) {
                deviceIds.push(deviceId);
            }
        }
        // Note that we sort the device ids in order to prepare for a possible future
        // change where we will compare each device id before emitting the devices
        // list change event.
        deviceIds.sort();
        if (deviceIds.length) {
            const activeDeviceId = cluster.active_device_id;
            for (let i = 0, l = deviceIds.length; i < l; i++) {
                const device = cluster.devices[deviceIds[i]];
                const formattedDevice = this._formatClusterDevice(device, activeDeviceId);
                this._lastKnownDevices.push(formattedDevice);
                if (formattedDevice.is_active) {
                    this._lastActiveDevice = formattedDevice;
                }
                if (formattedDevice.local) {
                    this._localDevice = formattedDevice;
                }
            }
        }
        if (!this._fetchedInitialState ||
            !areDevicesEqual(this._lastActiveDevice, prevActiveDevice)) {
            const playerState = cluster.player_state;
            const hasTransferrablePlayerState = playerState && isTransferrable(playerState);
            this.emit("device_state_changed" /* CONNECT_API_DEVICE_STATE_CHANGED */, {
                deviceState: this._lastActiveDevice,
                hasOrphanedState: !this._lastActiveDevice && !!hasTransferrablePlayerState,
                disappeared: !!prevActiveDevice && !this._lastActiveDevice,
            });
        }
        this.emit("devices_changed" /* CONNECT_API_DEVICES_CHANGED */, {
            devices: this._lastKnownDevices,
            localDevice: this._localDevice,
        });
    }
    /**
     * Formats an individual DeviceInfo object from the cluster into a DeviceInfo
     * object for Harmony.
     *
     * @param device - The Cluster DeviceInfo object.
     * @param activeDeviceId - The device id of the active device according
     * to the Cluster.
     * @return The formatted DeviceInfo object.
     */
    _formatClusterDevice(device, activeDeviceId) {
        const deviceMetadata = {};
        // Parse device metadata
        if (device && Array.isArray(device.metadata)) {
            for (let i = 0, l = device.metadata.length; i < l; i++) {
                const metadata = device.metadata[i];
                deviceMetadata[metadata.type] = metadata.metadata;
            }
        }
        const capabilities = device.capabilities || {};
        const deviceInfo = {
            hidden: false,
            id: device.device_id,
            is_active: device.device_id === activeDeviceId,
            is_controllable: !!capabilities.is_controllable,
            is_observable: !!capabilities.is_observable,
            local: device.device_id === this._localDeviceId,
            metadata: deviceMetadata,
            name: device.name,
            type: (device.device_type || DeviceType.UNKNOWN).toLowerCase(),
            version: device.device_software_version,
            volume: capabilities.disable_volume ? -1 : device.volume / GAIA_VOLUME,
        };
        return deviceInfo;
    }
    /**
     * Parses the player_state from a Cluster object.
     *
     * @param cluster - The Cluster object from the Connect State service.
     */
    _parseClusterPlayerState(cluster) {
        this._lastKnownPlayerState = null;
        const activeDeviceId = cluster.active_device_id;
        const activeDevice = activeDeviceId && cluster.devices[activeDeviceId];
        let playerState = cluster.player_state;
        if (playerState && playerState.track) {
            if (activeDevice &&
                activeDevice.capabilities &&
                !activeDevice.capabilities.is_observable) {
                playerState = null;
            }
            else {
                // If there is no active device, we should mark the player state as not
                // playing.
                if (!activeDevice) {
                    playerState.is_paused = true;
                    if (playerState.restrictions) {
                        delete playerState.restrictions.disallow_resuming_reasons;
                        playerState.restrictions.disallow_pausing_reasons = [
                            'already_paused',
                        ];
                    }
                }
                // Due to protobuf-to-json translation, some of the fields are encoded as
                // strings instead of numbers. We therefore need to cast them before hand.
                playerState.duration = parseInt(playerState.duration, 10);
                playerState.position = parseInt(playerState.position, 10);
                playerState.position_as_of_timestamp = parseInt(playerState.position_as_of_timestamp, 10);
                playerState.timestamp = parseInt(playerState.timestamp, 10);
                playerState.next_tracks = playerState.next_tracks || [];
                playerState.prev_tracks = playerState.prev_tracks || [];
            }
            this._lastKnownPlayerState = playerState;
        }
        let isOrphaned = !activeDevice && !!this._lastKnownPlayerState;
        if (isOrphaned && playerState && !isTransferrable(playerState)) {
            // We want to ignore orphaned states that are non-transferrable.
            this._lastKnownPlayerState = null;
            isOrphaned = false;
        }
        this.emit("player_state_changed" /* CONNECT_API_PLAYER_STATE_CHANGED */, {
            playerState: this._lastKnownPlayerState,
            orphaned: isOrphaned,
            isLocal: !!activeDeviceId && activeDeviceId === this._localDeviceId,
        });
    }
    /**
     * Registers the PlayerAPIClient to the service in order to get state updates.
     *
     * @return A promise that will be resolved when the client registered successfully.
     */
    _register() {
        this._fetchedInitialState = false;
        const uri = `${this._endpoint}/v1/devices/${this._localObserverDeviceId}`;
        const payload = {
            member_type: 'CONNECT_STATE',
            device: {
                device_info: {
                    capabilities: {
                        can_be_player: false,
                        hidden: true,
                    },
                },
            },
        };
        if (!this._connectionId) {
            Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_CONNECTION_ID, 'Cannot register: no connection id.'));
        }
        return this._transport
            .request(uri, {
            method: 'PUT',
            headers: {
                'X-Spotify-Connection-Id': this._connectionId,
                'Accept': 'application/json',
            },
            payload: JSON.stringify(payload),
            responseType: 'json',
        })
            .then((response) => {
            if (response.status === 429) {
                this.emit("max_subscriptions_reached" /* CONNECT_API_MAX_SUBSCRIPTIONS_REACHED */, {
                    error: new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_MAX_SUBSCRIPTIONS_REACHED, 'Max connections reached'),
                });
            }
            else if (response.status === 200) {
                this.emit("registered" /* CONNECT_API_REGISTERED */, null);
                if (response.body) {
                    this._parseCluster(response.body);
                    this._fetchedInitialState = true;
                }
            }
        });
    }
    /**
     * Registers the PlayerAPIClient to the service in order to get state updates.
     *
     * @return A promise that will be resolved when the client registeres successfully.
     */
    register() {
        if (!this._connectionId) {
            return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_CONNECTION_ID, 'Cannot register: no connection id.'));
        }
        return this._descriptorPromise.then(this._register.bind(this));
    }
    /**
     * Deregisters the client from the service.
     *
     * @return A promise that will be resolved once the client has successfully deregistered.
     */
    deregister() {
        const uri = `${this._endpoint}/v1/devices/${this._localObserverDeviceId}`;
        return this._transport
            .request(uri, {
            method: 'DELETE',
            responseType: 'json',
        })
            .then((response) => {
            if (response.status === 200) {
                this.emit("deregistered" /* CONNECT_API_DEREGISTERED */, null);
            }
        });
    }
    /**
     * Returns a list of devices for the current user.
     *
     * @return The formatted list of devices.
     */
    getDevices() {
        if (!this._fetchedInitialState) {
            return new Promise((resolve) => {
                this.once("device_state_changed" /* CONNECT_API_DEVICE_STATE_CHANGED */, () => {
                    resolve({
                        devices: this._lastKnownDevices,
                        localDevice: this._localDevice,
                    });
                });
            });
        }
        return Promise.resolve({
            devices: this._lastKnownDevices,
            localDevice: this._localDevice,
        });
    }
    /**
     * Returns the DeviceState for the last active device.
     *
     * @return The last active device.
     */
    getActiveDevice() {
        if (!this._fetchedInitialState) {
            return new Promise((resolve) => {
                this.once("device_state_changed" /* CONNECT_API_DEVICE_STATE_CHANGED */, (e) => {
                    resolve(e.data.deviceState);
                });
            });
        }
        return Promise.resolve(this._lastActiveDevice);
    }
    /**
     * Returns the last ContextPlayerState.
     *
     * @return The last ContextPlayerState.
     */
    getContextPlayerState() {
        if (!this._fetchedInitialState) {
            return new Promise((resolve) => {
                this.once("player_state_changed" /* CONNECT_API_PLAYER_STATE_CHANGED */, (e) => {
                    resolve(e.data.playerState);
                });
            });
        }
        return Promise.resolve(this._lastKnownPlayerState);
    }
    /**
     * Plays a ContextDescriptor
     *
     * @param  descriptor - The descriptor to play.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    play(descriptor, deviceId = null) {
        if (!descriptor) {
            return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_INVALID_ARGUMENTS, 'Descriptor is required for play commands'));
        }
        return this._sendPlayerCommand('play', deviceId, descriptor);
    }
    /**
     * Triggers the target device id to update its context.
     *
     * @param deviceId - The target device id.
     * @param options - The options for update.
     * @return True if the device was updated, false otherwise.
     */
    updateCurrentContext(deviceId = null, options = {}) {
        return this.getContextPlayerState().then((state) => {
            if (!state) {
                return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_STATE, 'Nothing playing'));
            }
            if (!state.session_id) {
                return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_SESSION_ID, 'The current state does not have a session id'));
            }
            if (!state.context_url && !options.forceNonUpdateable) {
                // If there is no context_url, the context was probably resolved by
                // the origin of the context rather than by the backend. In which case,
                // calls to update the context would most likely result in a weird
                // state. Disable this behaviour unless the user chose to do it.
                return Promise.resolve(false);
            }
            return this._sendPlayerCommand('update_context', deviceId, {
                context: {
                    url: state.context_url || `context://${state.context_uri}`,
                },
                session_id: state.session_id,
            });
        });
    }
    /**
     * Sets the Player queue.
     *
     * @param  queue - The queue to set.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    setQueue(queue, deviceId) {
        return this._sendPlayerCommand('set_queue', deviceId, queue);
    }
    /**
     * Appends a track to the queue.
     *
     * @param track - The track to add to the queue.
     * @param deviceId - The target device id. If omitted, the last active
     *   device id is used.
     * @return True if the operation was sent successfully.
     */
    addToQueue(track, deviceId) {
        return this._sendPlayerCommand('add_to_queue', deviceId, { track });
    }
    /**
     * Toggles the playback state of the device.
     *
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    togglePlay(deviceId) {
        return this.getContextPlayerState().then((state) => {
            if (!state) {
                return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_STATE, 'Nothing playing'));
            }
            return state.is_paused ? this.resume(deviceId) : this.pause(deviceId);
        });
    }
    /**
     * Transfers the playback from the current active device to a target device.
     *
     * @param deviceId - The target device id. If omitted, the local device
     * id is used.
     * @param  options - The options for transfer.
     * @return True if the operation was sent successfully.
     */
    transfer(deviceId, options = {}) {
        const payload = {
            transfer_options: {
                restore_paused: 'restore',
            },
        };
        if (options && 'initially_paused' in options) {
            payload.transfer_options.restore_paused = options.initially_paused
                ? 'pause'
                : 'resume';
        }
        return this._sendConnectCommand('transfer', deviceId || LOCAL_DEVICE_ID, payload);
    }
    /**
     * Pauses the current playback on the target device.
     *
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    pause(deviceId) {
        return this._sendPlayerCommand('pause', deviceId);
    }
    /**
     * Resumes the current playback on the target device.
     *
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    resume(deviceId) {
        return this._sendPlayerCommand('resume', deviceId);
    }
    /**
     * Skips the playback of the target device to the next track.
     *
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @param options - The skip options.
     * @return True if the operation was sent successfully.
     */
    nextTrack(deviceId, options) {
        return this._sendPlayerCommand('skip_next', deviceId, options);
    }
    /**
     * Skips the playback of the target device to the previous track.
     *
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @param options - The skip options.
     * @return True if the operation was sent successfully.
     */
    previousTrack(deviceId, options) {
        return this._sendPlayerCommand('skip_prev', deviceId, options);
    }
    /**
     * Changes the track position of the currently playing track of the target
     * device.
     *
     * @param position - The new position in milliseconds.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    seek(position, deviceId) {
        if (isNaN(position) || position < 0) {
            return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_INVALID_POSITION, 'Invalid position.'));
        }
        return this._sendPlayerCommand('seek_to', deviceId, { value: position });
    }
    /**
     * Sets the shuffle mode of the current track.
     *
     * @param shuffle - When set to true, the context will be played shuffled.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    setShuffle(shuffle, deviceId) {
        return this._sendPlayerCommand('set_shuffling_context', deviceId, {
            value: !!shuffle,
        });
    }
    toggleShuffle(deviceId) {
        return this.getContextPlayerState().then((state) => {
            if (!state) {
                return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_NO_STATE, 'Nothing playing'));
            }
            const isShuffled = state.options && state.options.shuffling_context;
            return this.setShuffle(!isShuffled, deviceId);
        });
    }
    /**
     * Sets the repeating context mode of the current track.
     *
     * @param value - When set to true, the context will be played on
     * repeat, playing the first track at the end of the context.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    setRepeatingContext(value, deviceId) {
        return this._sendPlayerCommand('set_repeating_context', deviceId, {
            value,
        });
    }
    /**
     * Sets the repeating track mode of the current track.
     *
     * @param value - When set to true, tracks will be played on repeat
     * mode, replaying the same track after it has ended.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    setRepeatingTrack(value, deviceId) {
        return this._sendPlayerCommand('set_repeating_track', deviceId, {
            value: !!value,
        });
    }
    /**
     * Sets the ContextPlayerOptions for a context.
     *
     * @param options - The options to set.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    setOptions(options, deviceId) {
        return this._sendPlayerCommand('set_options', deviceId, Object.assign({}, options));
    }
    /**
     * Sets the volume of the target device.
     *
     * @param volume - The volume as a number from 0 to 1.0.
     * @param deviceId - The target device id. If omitted, the last active
     * device id is used.
     * @return True if the operation was sent successfully.
     */
    setVolume(volume, deviceId) {
        if (isNaN(volume) || volume < 0) {
            return Promise.reject(new PlayerAPIClientError(HarmonyErrors.CONNECTAPI_CLIENT_INVALID_VOLUME, 'Invalid volume.'));
        }
        // Gaia volume needs to be an int, so we need to round it.
        const gaiaVolume = Math.round(volume * GAIA_VOLUME);
        return this._sendConnectCommand('volume', deviceId, { volume: gaiaVolume });
    }
    /**
     * Logs out the target device.
     *
     * @param deviceId - The target device id. If omitted, the last active
     *   device id is used.
     * @return True if the operation was sent successfully.
     */
    logout(deviceId) {
        return this._sendConnectCommand('logout', deviceId);
    }
}
//# sourceMappingURL=client.js.map